import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { sqWorkbookStore } from '@/core/core.stores';
import { WORKBOOK_VERSIONED_ITEM_TYPES } from '@/core/versionHistory.constants';
import { sqAuditApi } from '@seeqdev/api/endpoint/AuditApi';
import type { VersionHistoryOutputV1 } from '@seeqdev/api/model/VersionHistoryOutputV1';

export type VersionHistorySelectOption = {
  label: string;
  value: VersionHistoryOutputV1;
};

export const enum VersionHistoryOperations {
  Update = 'update',
  Create = 'create',
}

/**
 * Normalizes a workbook or version history name for comparison by stripping:
 * - Version suffixes like " v.1", " v.2", "V1", "V2"
 * - Translation key patterns like "ACCESS_CONTROL.LOCKING.VERSIONING.V1"
 * - Copy suffixes like " - Copy", " - Copy (2)", " Copy", " Copy (3)"
 *
 * This allows matching "My Analysis v.1 - Copy" to version history "My Analysis"
 */
export const normalizeVersionHistoryName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/access_control\.locking\.versioning\.v\d+/gi, '') // Remove translation key
    .replace(/\s*v\.?\s*\d+/gi, '') // Remove " v.1", " v1", "V.2", "V2", etc.
    .replace(/\s*-\s*copy(\s*\(\d+\))?$/i, '') // Remove " - Copy", " - Copy (2)", etc.
    .replace(/\s+copy(\s*\(\d+\))?$/i, '') // Remove " Copy", " Copy (2)", etc.
    .trim();

/**
 * A hook for managing the state of version history operations in the application.
 *
 * @return an object with the current version history operation, selected version history, new workbook name,
 * functions to set the new workbook name, notes, operation type, selected version history,
 * and a list of writeable version history options.
 */
export const useVersionState = () => {
  const [versionState, setVersionState] = useState({
    operation: VersionHistoryOperations.Create,
    selectedOption: null as VersionHistorySelectOption | null,
    newWorkbookName: sqWorkbookStore.name,
    newWorkbookNameError: null as string | null,
    notes: '',
    versionHistories: [] as VersionHistoryOutputV1[],
    writeableVersionHistories: [] as VersionHistoryOutputV1[],
    writeableVersionHistoryOptions: [] as VersionHistorySelectOption[],
    relatedVersionHistories: [] as VersionHistoryOutputV1[],
    hasFetched: false,
    hasAutoSet: false,
  });

  useEffect(() => {
    const fetchVersionHistories = async () => {
      try {
        const [relatedVersionHistories, allVersionHistories] = await Promise.all([
          sqAuditApi.getVersionHistories({
            duplicateItemId: sqWorkbookStore.workbookId,
            versionedItemType: WORKBOOK_VERSIONED_ITEM_TYPES,
          }),
          sqAuditApi.getVersionHistories({ versionedItemType: WORKBOOK_VERSIONED_ITEM_TYPES }),
        ]);
        const allHistories = allVersionHistories.data.versionHistories;
        const relatedHistories = relatedVersionHistories.data.versionHistories;
        const relatedIds = new Set(relatedHistories.map((history) => history.id));

        // By default, the endpoint returns all version histories with READ or higher permission
        // But for this dropdown, we only want the ones the user can WRITE or ones that belong to the EVERYONE group
        const writeableVersionHistories = allHistories.filter(
          (history) => history.effectivePermissions?.write === true,
        );

        const writeableVersionHistoryOptions: VersionHistorySelectOption[] =
          writeableVersionHistories?.map((item) => ({
            label: `${item.name} ${t('ACCESS_CONTROL.LOCKING.VERSIONING.V')}${item.versions.length + 1}`,
            value: item,
          })) ?? [];

        setVersionState((prev) => ({
          ...prev,
          versionHistories: allHistories,
          writeableVersionHistories: writeableVersionHistories.filter(Boolean) as VersionHistoryOutputV1[],
          writeableVersionHistoryOptions,
          relatedVersionHistories: allHistories.filter((vh) => relatedIds.has(vh.id)),
          hasFetched: true,
        }));
      } catch (error) {
        console.error('Failed to fetch version histories:', error);
      }
    };

    fetchVersionHistories();
  }, []);

  useEffect(() => {
    if (!versionState.hasFetched || versionState.hasAutoSet) return;

    // Find writeable version histories that are related to this workbook (via duplicateItemId API)
    const writeableRelatedHistories = versionState.writeableVersionHistoryOptions.filter((option) =>
      versionState.relatedVersionHistories.some((related) => related.id === option.value.id),
    );

    // Use name matching as a fallback or tiebreaker
    const workbookNameBase = normalizeVersionHistoryName(sqWorkbookStore.name);
    const bestNameMatch = versionState.writeableVersionHistoryOptions.find(
      (option) => normalizeVersionHistoryName(option.label) === workbookNameBase,
    );

    // Priority 1: If there's exactly one writeable related version history, use it (trust the API)
    // Priority 2: If multiple related, try to find the best name match among them
    // Priority 3: Fall back to name matching if no related histories found
    let selectedOption: VersionHistorySelectOption | null = null;

    if (writeableRelatedHistories.length === 1) {
      // Trust the API - exactly one related version history
      selectedOption = writeableRelatedHistories[0];
    } else if (writeableRelatedHistories.length > 1) {
      // Multiple related - use name matching to pick the best one
      const bestRelatedMatch = writeableRelatedHistories.find(
        (option) => normalizeVersionHistoryName(option.label) === workbookNameBase,
      );
      selectedOption = bestRelatedMatch ?? writeableRelatedHistories[0];
    } else if (bestNameMatch) {
      // No related histories from API, but name matching found one
      selectedOption = bestNameMatch;
    }

    if (selectedOption) {
      setVersionState((prev) => ({
        ...prev,
        operation: VersionHistoryOperations.Update,
        selectedOption,
        hasAutoSet: true,
      }));
    } else {
      setVersionState((prev) => ({
        ...prev,
        operation: VersionHistoryOperations.Create,
        hasAutoSet: true,
      }));
    }
  }, [
    versionState.hasFetched,
    versionState.hasAutoSet,
    versionState.relatedVersionHistories,
    versionState.writeableVersionHistoryOptions,
  ]);

  const handleSetNewWorkbookName = (name: string) => {
    const isDuplicateName = versionState.versionHistories.some(
      (versionHistory) => versionHistory.name.toLowerCase().trim() === name.toLowerCase().trim(),
    );
    const errorMessage = isDuplicateName ? t('ACCESS_CONTROL.LOCKING.VERSIONING.DUPLICATE_NAME_ERROR') : null;
    setVersionState((prev) => ({ ...prev, newWorkbookName: name, newWorkbookNameError: errorMessage }));
  };

  const handleSetNotes = (notes: string | undefined) => setVersionState((prev) => ({ ...prev, notes: notes ?? '' }));
  const handleSetOperation = (operation: VersionHistoryOperations) =>
    setVersionState((prev) => ({ ...prev, operation }));

  const handleSelectVersionHistory = (option: VersionHistorySelectOption) =>
    setVersionState((prev) => ({ ...prev, selectedOption: option }));

  return {
    versionHistoryOperation: versionState.operation,
    selectedVersionHistory: versionState.selectedOption,
    newWorkbookName: versionState.newWorkbookName,
    newWorkbookNameError: versionState.newWorkbookNameError,
    handleSetNewWorkbookName,
    notes: versionState.notes,
    handleSetNotes,
    handleSetOperation,
    handleSelectVersionHistory,
    versionHistories: versionState.versionHistories,
    writeableVersionHistoryOptions: versionState.writeableVersionHistoryOptions,
  };
};

export type VersionState = ReturnType<typeof useVersionState>;
